/* eslint-disable react/prop-types */
import React, { useState, useMemo } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Box from '@mui/material/Box';
import { ProblemStage } from '../common/page-info';
import { ConstructData } from '../page-data/construct-data';
import { getImageUrlFromScenario } from '../common/helpers';

import { ITSEventType, ReviewClickEventData } from '../common/page-data';
import { ProblemOption } from '../common/problem-option';

import { Review } from '../common-components/review';
import { TagScenario } from '../common-components/tag-scenario';
import { PageComp } from './page-comp-props';

/**
 * These are problem type 6
 * 
 * @param props 
 * @returns 
 */
export const ProblemConstructComp: PageComp = (props) => {
  const { isTeacherView, stage, onReviewed, onChange, tagIdsToEmphasize } = props;
  const pageData = props.pageData as ConstructData;
  const { options, skill, scenario, problemQuality } = pageData;

  const [selections, setSelections] = useState<string[]>([]);

  const onSelChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSel: string[]
  ) => {
    if (!isReview) {
      setSelections(newSel);

      for (const v of options) {
        const newSelState = newSel.includes(v.id);
        if (v.isSelected !== newSelState) {
          pageData.recordEvent(ITSEventType.selectOption, {
            id: v.id,
            selected: newSelState,
            correctOption: v.isCorrectResponse,
            tags: v.tags,
          });
          if (onChange) onChange(v);
        }
        v.isSelected = newSelState;
      }
    }
  };

  const onFeedbackReviewed = (option: ProblemOption): void => {
    const data: ReviewClickEventData = {
      id: option.id,
    };
    pageData.recordEvent(ITSEventType.reviewClick, data);
    if (onReviewed) onReviewed(option);
  };

  const isReview = stage === ProblemStage.ShowResult;

  const imgUrl: string | undefined = getImageUrlFromScenario(scenario);
  const background: string | undefined = scenario?.metadata.patient.background;
  const content: string | JSX.Element =
    scenario?.content?.markdown || scenario?.content.text || '';

  const indexOfOptionToReview = isReview
    ? options.findIndex((value) => !value.isAnsweredCorrectly)
    : -1;
  const isSkillWithStep = useMemo(() => {
    const skillsWithSteps = [
      "Assigning Homework",
      "Reviewing Homework",
      "Assessing Suicide Risk",
      "Managing Mild to Moderate Suicide Risk",
      "Managing Severe Suicide Risk"
    ];
    return skillsWithSteps.indexOf(skill.id) >= 0;
  }, [skill.id]);
  const stepsText = (skill.id === "Assessing Suicide Risk") ?
    'next step' : 'step';
  const deemphasizeResponses = tagIdsToEmphasize && tagIdsToEmphasize.length > 0;
  return (
    <span>
      <TagScenario
        imgUrl={imgUrl}
        background={background}
        content={content}
        description={
          <span>
            Select <b>all</b> statements below that demonstrate{' '}
            {isSkillWithStep && problemQuality
              ? `the appropriate ${stepsText} of `
              : ''}
            {skill.name.toLocaleLowerCase()}
            {!problemQuality && <b> poorly</b>}.
          </span>
        }
      />

      <ToggleButtonGroup
        value={selections}
        color={deemphasizeResponses ? 'standard' : 'primary'}
        orientation='vertical'
        onChange={onSelChange}
        fullWidth
        sx={{
          mt: 2,
          mb: 1,
          '& .MuiToggleButtonGroup-grouped': {
            mb: 0.5,
          },
        }}
      >
        {options.map((option, index) => {
          // For teacher dashboard, we need to de-emphasize a response if it is not associated
          // with a given tag.
          let deemphasizeThisOption = false;
          if (deemphasizeResponses && tagIdsToEmphasize) {
            const matchedTag = option.tags.find((value) => {
              return tagIdsToEmphasize?.indexOf(value.id) >= 0
            });
            deemphasizeThisOption = (matchedTag == undefined);
          }
          
          return <ToggleButton
            color={deemphasizeThisOption ? 'standard' : 'primary'}
            selected={option.isSelected}
            fullWidth
            value={option.id}
            key={option.id}
            sx={{
              display: 'inline-flex',
              justifyContent: 'space-between',
              padding: 0,
              pointerEvents: isReview ? 'none' : 'auto',
            }}
          >
            <Box sx={{ p: 2, textAlign: 'start' }}>{option.text}</Box>
            {isReview && (
              <Review
                hasRequestReviewPrompt={!isTeacherView && index === indexOfOptionToReview}
                isCorrectResponse={option.isCorrectResponse}
                isSelected={option.isSelected}
                onReviewed={() => onFeedbackReviewed(option)}
                reason={option?.reasonIncorrect}
                deemphasize={deemphasizeThisOption}
              />
            )}
          </ToggleButton>;
        })}
      </ToggleButtonGroup>
    </span>
  );
};

export default ProblemConstructComp;
